import { fetchJson } from '@api/ApiHelper';
import { ApiData, ApiResponseObject } from '@api/v4/ApiResponseTypes';

export type ApiKeyServerStatus =
  | 'active'
  | 'expiring_soon'
  | 'deactivated'
  | 'revoked';

export type ApiKeySource = 'legacy' | 'managed';

export interface ApiKeyRotatedFrom {
  id: number;
  token_prefix: string;
  type: string;
}

export interface ApiKeyCreatedByUser {
  email: string;
  id: number;
  name: string;
  user_key: string;
}

/** JSON:API `attributes` payload for a single api_key resource. */
export interface ApiKeyAttributes {
  created_at: string;
  created_by_user: ApiKeyCreatedByUser | null;
  description: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  name: string;
  revoked_at: string | null;
  rotated_from: ApiKeyRotatedFrom | null;
  source: ApiKeySource;
  status: ApiKeyServerStatus;
  token_prefix: string;
}

export type ApiKeyResource = ApiResponseObject<ApiKeyAttributes, 'api_key'>;

export type ApiKeysListResponse = ApiData<ApiKeyAttributes, 'api_key'>;

export interface GetApiKeysParams {
  order?: 'asc' | 'desc';
  page?: number;
  per?: number;
  sort?: string;
  /** Filters by one or more statuses; sent as repeated `status[]=` params. */
  status?: ApiKeyServerStatus[];
}

/**
 * Fetches the managed API keys for an organization. Sorting, filtering, and
 * pagination are handled server-side.
 * GET /api/v4/organizations/:organization_key/api_keys
 */
export const getApiKeys = async (
  organizationKey: string,
  { order, page, per, sort, status = [] }: GetApiKeysParams = {}
): Promise<ApiKeysListResponse> => {
  // Built by hand because `fetchJson` joins array params with commas, but the
  // API expects repeated `status[]=active&status[]=expiring_soon` params.
  const query = new URLSearchParams();
  if (per !== undefined) query.append('per', String(per));
  if (page !== undefined) query.append('page', String(page));
  if (sort) query.append('sort', sort);
  if (order) query.append('order', order);
  status.forEach((value) => query.append('status[]', value));

  const queryString = query.toString();

  return fetchJson<ApiKeysListResponse>({
    method: 'GET',
    url: `/api/v4/organizations/${organizationKey}/api_keys${queryString ? `?${queryString}` : ''}`,
  });
};
